import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BRAND_NAME, SUPPORT_EMAIL, CONTACT_EMAIL } from "@/config/appConfig";
import {
  ArrowLeft,
  Building2,
  Settings,
  FileText,
  Save,
  Upload,
  Eye,
  Edit,
  RefreshCw,
  Palette,
  Globe,
  Phone,
  MapPin,
  CreditCard,
  Receipt,
  Image,
  X
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function SuperAdminConfig() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState("brand");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  
  // Brand Settings
  const [brandSettings, setBrandSettings] = useState({
    company_name: BRAND_NAME,
    tagline: "Interactive Seating Charts for Event Ticketing",
    contact_email: CONTACT_EMAIL,
    support_email: SUPPORT_EMAIL,
    contact_phone: "+44 20 1234 5678",
    address_line1: "",
    address_line2: "",
    city: "London",
    postcode: "",
    country: "United Kingdom",
    vat_number: "",
    vat_registered: false,
    company_registration: "",
    primary_color: "#7c3aed",
    secondary_color: "#4f46e5",
    accent_color: "#06b6d4",
    logo_url: null,
    logo_dark_url: null,
    favicon_url: null,
    email_logo_url: null
  });
  
  // Bill Settings (renamed from Invoice)
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_prefix: "BILL",
    next_invoice_number: 1001,
    show_vat: true,
    vat_rate: 20.0,
    prices_include_vat: true,  // Prices shown to users include VAT
    invoice_notes: "Thank you for choosing SeatPilot!",
    payment_terms: "Payment due within 30 days",
    bank_name: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_sort_code: "",
    bank_iban: "",
    bank_swift: "",
    include_bank_details: false
  });
  
  // Social Links
  const [socialLinks, setSocialLinks] = useState({
    twitter: "",
    facebook: "",
    linkedin: "",
    instagram: "",
    youtube: ""
  });
  
  // Legal Pages
  const [legalPages, setLegalPages] = useState({});
  const [editingLegal, setEditingLegal] = useState(null);
  const [legalContent, setLegalContent] = useState({ title: "", content: "" });

  // Check access
  useEffect(() => {
    if (user && user.role !== "super_admin") {
      toast.error("Access denied");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/settings`, { withCredentials: true });
      const settings = res.data.settings || {};
      
      if (settings.brand) setBrandSettings(prev => ({ ...prev, ...settings.brand }));
      if (settings.invoice) setInvoiceSettings(prev => ({ ...prev, ...settings.invoice }));
      if (settings.social) setSocialLinks(prev => ({ ...prev, ...settings.social }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLegalPages = useCallback(async () => {
    try {
      const [terms, privacy, cookies, refund] = await Promise.all([
        axios.get(`${API}/platform/legal/terms`),
        axios.get(`${API}/platform/legal/privacy`),
        axios.get(`${API}/platform/legal/cookies`),
        axios.get(`${API}/platform/legal/refund`)
      ]);
      setLegalPages({
        terms: terms.data.page,
        privacy: privacy.data.page,
        cookies: cookies.data.page,
        refund: refund.data.page
      });
    } catch (error) {
      console.error("Error fetching legal pages:", error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchLegalPages();
  }, [fetchSettings, fetchLegalPages]);

  // Save all settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/platform/settings/brand`, brandSettings, { withCredentials: true }),
        axios.put(`${API}/platform/settings/invoice`, invoiceSettings, { withCredentials: true }),
        axios.put(`${API}/platform/settings/social`, socialLinks, { withCredentials: true })
      ]);
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Save legal page
  const saveLegalPage = async () => {
    if (!editingLegal) return;
    try {
      await axios.put(`${API}/platform/legal/${editingLegal}`, {
        page_type: editingLegal,
        title: legalContent.title,
        content: legalContent.content
      }, { withCredentials: true });
      toast.success("Page updated!");
      setEditingLegal(null);
      fetchLegalPages();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  // Upload logo
  const handleLogoUpload = async (logoType, file) => {
    if (!file) return;
    setUploadingLogo(logoType);
    try {
      const formData = new FormData();
      formData.append("logo_type", logoType);
      formData.append("file", file);
      
      const res = await axios.post(`${API}/platform/settings/logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Logo uploaded successfully!");
      
      // Update local state with new URL
      const fieldMap = { main: "logo_url", main_dark: "logo_dark_url", favicon: "favicon_url", email: "email_logo_url" };
      setBrandSettings(prev => ({ ...prev, [fieldMap[logoType]]: res.data.logo_url }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(null);
    }
  };

  // Delete logo
  const handleLogoDelete = async (logoType) => {
    if (!window.confirm("Are you sure you want to delete this logo?")) return;
    try {
      await axios.delete(`${API}/platform/settings/logo/${logoType}`, { withCredentials: true });
      toast.success("Logo deleted!");
      
      const fieldMap = { main: "logo_url", main_dark: "logo_dark_url", favicon: "favicon_url", email: "email_logo_url" };
      setBrandSettings(prev => ({ ...prev, [fieldMap[logoType]]: null }));
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const tabs = [
    { id: "brand", label: "Brand & Company", icon: Building2 },
    { id: "invoice", label: "Bill Settings", icon: Receipt },
    { id: "legal", label: "Legal Pages", icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/backoffice/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-600" />
                  Platform Settings
                </h1>
                <p className="text-sm text-gray-500">Configure your SeatPilot platform</p>
              </div>
            </div>
            {activeTab !== "legal" && (
              <Button onClick={saveSettings} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? "bg-violet-100 text-violet-700"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Card className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading settings...</p>
          </Card>
        ) : (
          <>
            {/* Brand & Company Tab */}
            {activeTab === "brand" && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-violet-600" />
                    Company Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                      <Input
                        value={brandSettings.company_name}
                        onChange={(e) => setBrandSettings({ ...brandSettings, company_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                      <Input
                        value={brandSettings.tagline}
                        onChange={(e) => setBrandSettings({ ...brandSettings, tagline: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                      <Input
                        type="email"
                        value={brandSettings.contact_email}
                        onChange={(e) => setBrandSettings({ ...brandSettings, contact_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
                      <Input
                        type="email"
                        value={brandSettings.support_email}
                        onChange={(e) => setBrandSettings({ ...brandSettings, support_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <Input
                        value={brandSettings.contact_phone}
                        onChange={(e) => setBrandSettings({ ...brandSettings, contact_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <Input
                        value={brandSettings.country}
                        onChange={(e) => setBrandSettings({ ...brandSettings, country: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-md font-medium mt-6 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Address
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                      <Input
                        value={brandSettings.address_line1}
                        onChange={(e) => setBrandSettings({ ...brandSettings, address_line1: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                      <Input
                        value={brandSettings.address_line2}
                        onChange={(e) => setBrandSettings({ ...brandSettings, address_line2: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <Input
                        value={brandSettings.city}
                        onChange={(e) => setBrandSettings({ ...brandSettings, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <Input
                        value={brandSettings.postcode}
                        onChange={(e) => setBrandSettings({ ...brandSettings, postcode: e.target.value })}
                      />
                    </div>
                  </div>

                  <h3 className="text-md font-medium mt-6 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> VAT & Registration
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                      <Input
                        value={brandSettings.vat_number}
                        onChange={(e) => setBrandSettings({ ...brandSettings, vat_number: e.target.value })}
                        placeholder="GB123456789"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Registration</label>
                      <Input
                        value={brandSettings.company_registration}
                        onChange={(e) => setBrandSettings({ ...brandSettings, company_registration: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <Checkbox
                        checked={brandSettings.vat_registered}
                        onCheckedChange={(checked) => setBrandSettings({ ...brandSettings, vat_registered: checked })}
                      />
                      <label className="text-sm text-gray-700">VAT Registered</label>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-violet-600" />
                    Brand Colors
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: "primary_color", label: "Primary" },
                      { key: "secondary_color", label: "Secondary" },
                      { key: "accent_color", label: "Accent" }
                    ].map(color => (
                      <div key={color.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{color.label} Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={brandSettings[color.key] || "#7c3aed"}
                            onChange={(e) => setBrandSettings({ ...brandSettings, [color.key]: e.target.value })}
                            className="w-12 h-10 rounded border cursor-pointer"
                          />
                          <Input
                            value={brandSettings[color.key] || ""}
                            onChange={(e) => setBrandSettings({ ...brandSettings, [color.key]: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Logo Upload Section */}
                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-violet-600" />
                    Logo & Branding Images
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Upload your company logos. Recommended formats: PNG, SVG, WEBP. Max size: 2MB.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Main Logo (Light) */}
                    <div className="border rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Logo (Light Background)
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Used on light backgrounds throughout the platform</p>
                      
                      {brandSettings.logo_url ? (
                        <div className="relative group">
                          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[100px]">
                            <img 
                              src={`${BACKEND_URL}${brandSettings.logo_url}`} 
                              alt="Main Logo" 
                              className="max-h-20 max-w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoDelete("main")}
                            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("main", e.target.files[0])}
                            disabled={uploadingLogo === "main"}
                          />
                        </label>
                      )}
                      {uploadingLogo === "main" && (
                        <div className="mt-2 text-center text-sm text-violet-600">Uploading...</div>
                      )}
                    </div>

                    {/* Dark Logo */}
                    <div className="border rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Logo (Dark Background)
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Used on dark backgrounds (e.g., footer, dark mode)</p>
                      
                      {brandSettings.logo_dark_url ? (
                        <div className="relative group">
                          <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-center min-h-[100px]">
                            <img 
                              src={`${BACKEND_URL}${brandSettings.logo_dark_url}`} 
                              alt="Dark Logo" 
                              className="max-h-20 max-w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoDelete("main_dark")}
                            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors bg-gray-800">
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-400">Click to upload</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml,image/webp"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("main_dark", e.target.files[0])}
                            disabled={uploadingLogo === "main_dark"}
                          />
                        </label>
                      )}
                      {uploadingLogo === "main_dark" && (
                        <div className="mt-2 text-center text-sm text-violet-600">Uploading...</div>
                      )}
                    </div>

                    {/* Favicon */}
                    <div className="border rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Favicon
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Browser tab icon (recommended: 32x32 or 64x64 px)</p>
                      
                      {brandSettings.favicon_url ? (
                        <div className="relative group">
                          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[80px]">
                            <img 
                              src={`${BACKEND_URL}${brandSettings.favicon_url}`} 
                              alt="Favicon" 
                              className="max-h-12 max-w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoDelete("favicon")}
                            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors">
                          <Upload className="w-6 h-6 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload</span>
                          <input
                            type="file"
                            accept="image/png,image/x-icon,image/svg+xml"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("favicon", e.target.files[0])}
                            disabled={uploadingLogo === "favicon"}
                          />
                        </label>
                      )}
                      {uploadingLogo === "favicon" && (
                        <div className="mt-2 text-center text-sm text-violet-600">Uploading...</div>
                      )}
                    </div>

                    {/* Email Logo */}
                    <div className="border rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Logo
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Used in email templates (recommended: 200px width)</p>
                      
                      {brandSettings.email_logo_url ? (
                        <div className="relative group">
                          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[80px]">
                            <img 
                              src={`${BACKEND_URL}${brandSettings.email_logo_url}`} 
                              alt="Email Logo" 
                              className="max-h-12 max-w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={() => handleLogoDelete("email")}
                            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors">
                          <Upload className="w-6 h-6 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("email", e.target.files[0])}
                            disabled={uploadingLogo === "email"}
                          />
                        </label>
                      )}
                      {uploadingLogo === "email" && (
                        <div className="mt-2 text-center text-sm text-violet-600">Uploading...</div>
                      )}
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-violet-600" />
                    Social Links
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["twitter", "facebook", "linkedin", "instagram", "youtube"].map(platform => (
                      <div key={platform}>
                        <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{platform}</label>
                        <Input
                          value={socialLinks[platform] || ""}
                          onChange={(e) => setSocialLinks({ ...socialLinks, [platform]: e.target.value })}
                          placeholder={`https://${platform}.com/seatpilot`}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Bill Settings Tab */}
            {activeTab === "invoice" && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-violet-600" />
                  Bill Configuration
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bill Prefix</label>
                    <Input
                      value={invoiceSettings.invoice_prefix}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_prefix: e.target.value })}
                      placeholder="BILL"
                    />
                    <p className="text-xs text-gray-500 mt-1">e.g. BILL-001001</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Bill Number</label>
                    <Input
                      type="number"
                      value={invoiceSettings.next_invoice_number}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, next_invoice_number: parseInt(e.target.value) || 1001 })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={invoiceSettings.show_vat}
                      onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, show_vat: checked })}
                    />
                    <label className="text-sm text-gray-700">Show VAT on Bills</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
                    <Input
                      type="number"
                      value={invoiceSettings.vat_rate}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, vat_rate: parseFloat(e.target.value) || 20 })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Checkbox
                        checked={invoiceSettings.prices_include_vat}
                        onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, prices_include_vat: checked })}
                      />
                      <div>
                        <label className="text-sm font-medium text-blue-800">Plan Prices Include VAT</label>
                        <p className="text-xs text-blue-600">
                          {invoiceSettings.prices_include_vat 
                            ? "Prices shown to customers already include VAT (e.g., £50 = £41.67 + VAT)" 
                            : "VAT will be added at checkout (e.g., £50 + £10 VAT = £60 total)"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Button 
                      variant="outline" 
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/invoices/admin/recalculate-all-invoices`, {}, { withCredentials: true });
                          if (response.data.success) {
                            toast({ title: "Success", description: response.data.message });
                          }
                        } catch (error) {
                          toast({ title: "Error", description: "Failed to recalculate invoices", variant: "destructive" });
                        }
                      }}
                      className="w-full"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Recalculate All Existing Invoices (Apply Current VAT Settings)
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      Click this after changing VAT settings to update all existing invoices
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Notes</label>
                    <Input
                      value={invoiceSettings.invoice_notes}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_notes: e.target.value })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                    <Input
                      value={invoiceSettings.payment_terms}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, payment_terms: e.target.value })}
                    />
                  </div>
                </div>

                <h3 className="text-md font-medium mt-6 mb-3">Bank Details (Optional)</h3>
                <div className="flex items-center gap-2 mb-4">
                  <Checkbox
                    checked={invoiceSettings.include_bank_details}
                    onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, include_bank_details: checked })}
                  />
                  <label className="text-sm text-gray-700">Include Bank Details on Invoices</label>
                </div>
                
                {invoiceSettings.include_bank_details && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                      <Input
                        value={invoiceSettings.bank_name}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                      <Input
                        value={invoiceSettings.bank_account_name}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_account_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                      <Input
                        value={invoiceSettings.bank_account_number}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_account_number: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort Code</label>
                      <Input
                        value={invoiceSettings.bank_sort_code}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, bank_sort_code: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Legal Pages Tab */}
            {activeTab === "legal" && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-600" />
                  Legal Pages
                </h2>
                <p className="text-sm text-gray-600 mb-4">Edit your legal documents. HTML formatting is supported.</p>
                
                <div className="space-y-3">
                  {[
                    { key: "terms", label: "Terms & Conditions" },
                    { key: "privacy", label: "Privacy Policy" },
                    { key: "cookies", label: "Cookie Policy" },
                    { key: "refund", label: "Refund Policy" }
                  ].map(page => (
                    <div key={page.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{page.label}</p>
                        <p className="text-sm text-gray-500">
                          Last updated: {legalPages[page.key]?.last_updated 
                            ? new Date(legalPages[page.key].last_updated).toLocaleDateString() 
                            : "Never"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/${page.key}`, "_blank")}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditingLegal(page.key);
                            setLegalContent({
                              title: legalPages[page.key]?.title || page.label,
                              content: legalPages[page.key]?.content || ""
                            });
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Legal Page Editor Dialog */}
      <Dialog open={!!editingLegal} onOpenChange={() => setEditingLegal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingLegal?.replace("_", " ").toUpperCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Page Title</label>
              <Input
                value={legalContent.title}
                onChange={(e) => setLegalContent({ ...legalContent, title: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Content (HTML supported)</label>
              <textarea
                className="w-full h-96 p-3 border rounded-lg font-mono text-sm"
                value={legalContent.content}
                onChange={(e) => setLegalContent({ ...legalContent, content: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingLegal(null)}>Cancel</Button>
              <Button onClick={saveLegalPage}>
                <Save className="w-4 h-4 mr-2" /> Save Page
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
